import { createElement, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { usePlaidLink } from 'react-plaid-link'
import { LinkAbortError } from '../linkAbandonReason'
import { clearPendingPlaidOAuth, plaidOAuthRedirectUri, savePendingPlaidOAuth } from './plaidOAuth'
import type { CompletionPayload, LinkAdapter, LinkSession, PrepareContext } from './types'

/**
 * The Plaid Link adapter. #6's Q14 decided "the npm package, not a CDN
 * script" and `react-plaid-link` (pinned exact, `5.0.0`) is that package —
 * version pinning and integrity through the lockfile for the WRAPPER code, a
 * named import, no `<script>` this app writes itself.
 *
 * **Correction to Q14's own reasoning, checked against the installed
 * package rather than assumed: `react-plaid-link` still loads Plaid's Link
 * UI from `https://cdn.plaid.com/link/v2/stable/link-initialize.js` at
 * runtime** (`node_modules/react-plaid-link/dist/index.js`'s own
 * `PLAID_LINK_STABLE_URL`, injected via `document.body.appendChild` the
 * first time `usePlaidLink` is called with an input). npm does not remove
 * the third-party script; it removes the CDN dependency for the *wrapper*
 * that talks to it, and gets that wrapper a real version pin the way a
 * hand-written `<script src="...">` tag never would. The hosting CSP
 * (enforced on app-dev, report-only elsewhere) allows `cdn.plaid.com` in
 * script-src and frame-src: a third-party origin genuinely executes in this
 * page, and would do so under Plaid's own un-pinned "stable" URL regardless
 * of which delivery method this repo chose. The policy allows the origin
 * rather than using a nonce, so `react-plaid-link`'s `cspNonce` is unused.
 *
 * **The same `link_token` serves both link and reauth (update mode).**
 * Whether a token opens Link's update mode is baked in server-side when the
 * backend mints it — nothing here has to know or branch on it, which is why
 * `useAggregator.ts`'s `createLinkSession`/`createReauthSession` both funnel
 * into the identical `adapter.open()` call.
 *
 * **No non-hook export exists.** `react-plaid-link`'s public surface
 * (`usePlaidLink`, `PlaidLink`, `PlaidEmbeddedLink` — checked against
 * `node_modules/react-plaid-link/dist/index.d.ts`) is React-hook/component
 * only; the lower-level `createPlaid`/`PlaidFactory` in `factory.ts` is not
 * re-exported. `LinkAdapter.open()` is a plain async function (`useAggregator
 * .ts`'s `useLinkFlow`/`useReauthFlow` are hooks themselves, but call this
 * from inside a `useCallback`, not from render), so bridging into the hook
 * needs a component — mounted into a detached, un-rendered `<div>` via
 * `createRoot`. This is this repo's own bridge, not a documented package
 * pattern — `node_modules/react-plaid-link/README.md` says neither
 * "createRoot" nor "promise" (checked, N3 architect review, #397; an
 * earlier version of this docblock claimed otherwise). Nothing here is
 * visible; Plaid's own Link UI is the iframe the SDK inserts once `open()`
 * fires.
 *
 * **The bridge is contained to this file, and settles (resolves/rejects)
 * on every exit path — success, cancel, SDK error, and a timeout for the
 * case the SDK never calls back at all.** `onDone` is the ONE place
 * settlement happens, reached from `onSuccess`, `onExit`, the hook's own
 * `error`, and `TIMEOUT_MS`; a `settled` flag makes it idempotent so two
 * callbacks racing cannot double-settle. Teardown (`root.unmount()` +
 * `container.remove()`) is DEFERRED one microtask past settlement (N1,
 * architect review) — `usePlaidLink`'s own `error` fires from a React
 * effect, and `ReactDOMRoot.prototype.unmount` warns when a root is
 * unmounted synchronously while React is still committing a passive
 * effect (checked against the installed `react-dom@19.2.8`'s own source,
 * not assumed). `onSuccess`/`onExit` fire from Plaid's iframe handler,
 * outside React, so they never hit this hazard — the deferral is uniform
 * across all four paths anyway, since a single mechanism that is always
 * safe beats one that is safe only for two of four callers. Unmounting is
 * not cosmetic regardless of timing: `usePlaidLink`'s OWN unmount effect
 * (its source, not this file's assumption) calls `exit({force:true})` then
 * `destroy()` on the underlying Plaid handler, so `root.unmount()` is also
 * what force-closes Plaid's own UI and releases its resources. What this
 * cannot do: force-close Plaid's overlay from OUTSIDE this promise (the
 * settled `LinkAdapter.open()` contract takes no cancellation signal, and
 * reshaping it was raised and declined — this plan's interfaces are
 * settled across Tickets 3–5). If the promise's consumer stops awaiting
 * it, the modal stays open until the user closes it or the timeout fires,
 * same as any other `usePlaidLink` caller anywhere.
 */
/**
 * A fixed, local fallback — never `plaidError.error_message`.
 * `PlaidLinkError.error_message` is Plaid's DEVELOPER-facing string and can
 * carry request identifiers; `display_message` is the sanctioned
 * user-facing one and may be `null` when Plaid has nothing to show a user
 * (team-lead review, #397). This is the copy for exactly that case, not a
 * generic catch-all — `display_message` still wins whenever Plaid supplies
 * one.
 */
const GENERIC_PLAID_ERROR = 'The connection could not be completed.'

/**
 * How long `PlaidLauncher` waits to reach `ready` before giving up on its
 * own — NOT how long the whole flow is allowed to take. Without this, a
 * silently blocked or unreachable `cdn.plaid.com` script (no network, an
 * extension, a CSP that omits `cdn.plaid.com`) never fires either
 * `usePlaidLink` callback, and the promise — and Plaid's own overlay,
 * appended to `document.body` outside this app's React tree — never
 * resolves at all (architect review, #397, "a timeout on the bridge").
 * Scoped to the ready-wait specifically so a user who is simply slow
 * inside Plaid's own UI, well past 15s, is never timed out for that. 15s:
 * comfortably past a slow script load, short enough that a genuinely stuck
 * load does not strand the user on "Connecting…" indefinitely.
 */
const READY_TIMEOUT_MS = 15_000

function PlaidLauncher({
  token,
  receivedRedirectUri,
  onDone,
}: {
  token: string
  /**
   * #414's OAuth resume — `undefined` for every ordinary `open()`, and the
   * exact URL the browser is currently on when `redirectFlow.resume`
   * mounts this after Plaid's own navigation back. Passed straight through
   * to `usePlaidLink` — this is `PlaidLinkOptionsWithLinkToken`'s own field
   * (`node_modules/react-plaid-link/dist/types/index.d.ts`, checked against
   * the installed package rather than assumed), which is what tells Link to
   * RESUME the session `token` already names instead of starting fresh.
   */
  receivedRedirectUri?: string
  onDone: (result: CompletionPayload | Error) => void
}) {
  const { open, ready, error } = usePlaidLink({
    token,
    receivedRedirectUri,
    onSuccess: (publicToken, metadata) => {
      if (publicToken === null) {
        // The completion CALLBACK's own payload was missing what it needed —
        // not a provider API response body (B2, architect review, #399: the
        // REASON travels as a structured field on the error now, never as
        // prose a different module has to pattern-match back out).
        onDone(new LinkAbortError('callback_malformed', 'Plaid did not return a token.'))
        return
      }
      onDone({ publicToken, metadata: metadata as unknown as Record<string, unknown> })
    },
    onExit: (plaidError) => {
      onDone(
        plaidError
          ? new LinkAbortError('provider_failure', plaidError.display_message ?? GENERIC_PLAID_ERROR)
          : new LinkAbortError('user_cancelled', 'The connection window was closed before finishing.'),
      )
    },
  })

  // Auto-opens the instant the SDK is ready — this component has no visible
  // affordance of its own, `Connect.tsx`'s own "Continue" button is what the
  // user already pressed to reach `adapter.open()` at all.
  useEffect(() => {
    if (ready) open()
  }, [ready, open])

  useEffect(() => {
    if (error) onDone(new LinkAbortError('provider_failure', GENERIC_PLAID_ERROR))
  }, [error, onDone])

  // The timeout guards reaching `ready`, not the whole flow — scoped here
  // rather than around the outer promise, because only this component can
  // see `ready`. Cleared by this same effect's cleanup the instant `ready`
  // flips true (a fresh effect run with an early return sets no new timer),
  // so a user who is simply slow inside Plaid's own UI after it loaded is
  // never timed out; only the SDK never loading at all is.
  useEffect(() => {
    if (ready) return
    const timeout = setTimeout(() => {
      onDone(
        new LinkAbortError(
          'presentation_unavailable',
          'This is taking too long — the bank could not be reached.',
        ),
      )
    }, READY_TIMEOUT_MS)
    return () => clearTimeout(timeout)
  }, [ready, onDone])

  return null
}

/**
 * The bridge both a fresh `open()` and an OAuth `redirectFlow.resume()`
 * mount through — one `createRoot`/settle/teardown implementation rather
 * than two, so N1/N2's timing guarantees (architect review, #397, this
 * file's own header) do not have to be proven twice.
 */
function launchPlaid(token: string, receivedRedirectUri: string | undefined): Promise<CompletionPayload> {
  return new Promise((resolve, reject) => {
    const container = document.createElement('div')
    document.body.appendChild(container)
    const root = createRoot(container)

    let settled = false
    function onDone(result: CompletionPayload | Error) {
      if (settled) return
      settled = true
      // Settle first, unmount after — N1 (architect review, #397).
      // `usePlaidLink`'s own `error` reaches here from a React passive
      // effect, and `root.unmount()` synchronously while React is still
      // committing one warns ("may lead to a race condition", checked
      // against the installed react-dom's own source). One microtask is
      // enough to land outside that window for every caller uniformly.
      if (result instanceof Error) reject(result)
      else resolve(result)
      queueMicrotask(() => {
        root.unmount()
        container.remove()
      })
    }

    root.render(createElement(PlaidLauncher, { token, receivedRedirectUri, onDone }))
  })
}

export const plaidAdapter: LinkAdapter = {
  open(session: LinkSession): Promise<CompletionPayload> {
    // Whatever `redirectFlow.prepare` persisted for THIS session is only
    // useful if Plaid actually navigates the browser away — every other
    // exit (success, abandon, an SDK error, the ready-timeout) means it
    // never left this page, so the persisted copy is now stale and is
    // cleared here rather than left to be read back by a later,
    // unrelated visit to `/plaid` (#414).
    return launchPlaid(session.linkToken, undefined).finally(() => clearPendingPlaidOAuth())
  },
  // Link's update mode needs no server call; POST /link-session/complete on a
  // Plaid reauth answers 409 already_linked (#397, epic #6 round 6).
  reauthRoundTripsToken: false,
  /**
   * #414 — one object, not three independent optional members (architect
   * review, B2). `redirectUri`, `prepare` and `resume` are one idea — "this
   * provider's flow can navigate the browser fully away and come back" —
   * and grouping them means a caller asks ONE question ("does this adapter
   * have a redirectFlow") instead of three, and `useAggregator.ts` reaches
   * `resume` through `getAdapter(pending.provider)` rather than importing
   * this module directly (the bypass B2 flagged: the generic funnel had
   * started reaching around `registry.ts`, the one place `registry.test.ts`
   * polices for exactly that).
   */
  redirectFlow: {
    // `null` in every environment but the one Plaid's dashboard has a
    // redirect URI registered for. See `plaidOAuth.ts`'s own docblock for
    // why this is a short allow-list rather than a `VITE_` env var.
    redirectUri: plaidOAuthRedirectUri,
    /**
     * `redirectUri` arrives ALREADY RESOLVED — the caller computed it one
     * line earlier to decide the request body — rather than this calling
     * `plaidOAuthRedirectUri()` a second time (security review, #414,
     * SEC-1). **No-ops when it is `null`**: with no `redirect_uri` sent on
     * this request, Plaid has no URL to send the browser back to, so a
     * write here would be unreadable BY CONSTRUCTION, not merely unread —
     * a token sitting in `sessionStorage` with no code path that will ever
     * consume it. Production, staging and a bare `npm run dev` on
     * localhost all take this branch; only the one registered dev origin
     * reaches the write below.
     */
    prepare(session: LinkSession, context: PrepareContext, redirectUri: string | null) {
      if (redirectUri === null) return
      const base = { linkToken: session.linkToken, provider: session.provider }
      switch (context.kind) {
        case 'link':
          savePendingPlaidOAuth({ kind: 'link', ...base, stateNonce: session.stateNonce })
          return
        case 'reauth':
          savePendingPlaidOAuth({ kind: 'reauth', ...base, itemId: context.itemId, stateNonce: session.stateNonce })
          return
        case 'relink':
          // Never persist a relink that could not be completed: the backend
          // refuses one without the nonce, and the return would reject it
          // anyway. Writing nothing lands the user on "no session found"
          // instead of a bank sign-in that ends in a 400.
          if (session.stateNonce === null) return
          savePendingPlaidOAuth({
            kind: 'relink',
            ...base,
            relinkItemId: context.relinkItemId,
            stateNonce: session.stateNonce,
            entryPoint: context.entryPoint,
            startedAt: context.startedAt,
          })
          return
      }
    },
    /**
     * `/plaid`'s own entry point (`PlaidOAuthReturn.tsx`, via
     * `useAggregator.ts`'s `usePlaidOAuthResume`) — re-launches Link with
     * the SAME `link_token` an earlier `open()` minted (round-tripped
     * through `sessionStorage` by `plaidOAuth.ts`, since the navigation
     * that brought the browser back here destroyed the in-memory value),
     * plus `receivedRedirectUri` set to the URL Plaid actually returned
     * to. Does NOT itself touch `sessionStorage` — its caller already
     * consumed the pending session via `usePendingPlaidOAuth()` before
     * calling this, so there is nothing left here to clear.
     */
    resume(linkToken: string, receivedRedirectUri: string): Promise<CompletionPayload> {
      return launchPlaid(linkToken, receivedRedirectUri)
    },
  },
}
